import * as Sentry from '@sentry/angular';
import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { ValidationError, validate } from 'class-validator';
import { AppModule } from './app/app.module';
import { CONFIG, Config } from './app/core/config/config';
import { installCsp } from './app/common/csp/csp-policy';
import { isChunkLoadError } from './app/core/error/chunk-load-error';
import { environment } from './environments/environment';
import { resolveSentryFeatureTag } from './app/core/integration/sentry/sentry-feature-tag';
import { shouldDropSentryError } from './app/core/integration/sentry/sentry-error-filter';
import { applySentryIdentityToEvent } from './app/core/integration/sentry/sentry-user-context';
import { SENTRY_RELEASE } from './sentry-release';

if (environment.production) {
  enableProdMode();
}

const resolveSentryEnvironmentFromConfig = (clientId: string | undefined): string => {
  if (!clientId) {
    return 'unknown';
  }

  return clientId;
};

// bankinglab.cloud is the shared hosting domain for per-tenant dev/QA deployments (e.g. bnk-ib-pvk-dev.bankinglab.cloud),
// as opposed to a tenant's own production domain (ib.bankera.com, ib.perlas.lt, etc.) - excluded to avoid Sentry noise
// from automated E2E test runs (Cypress) against these deployments.
const isDevEnv = window.location.hostname.endsWith('.bankinglab.cloud');
const isLocalEnv = window.location.hostname === 'localhost' || window.location.port === '3002' || isDevEnv;

void fetch('/ui-api/ui/settings')
  .then((response) => {
    const serverDate = response.headers.get('Date');
    if (serverDate) {
      sessionStorage.setItem('serverDate', serverDate);
    }
    return response.json();
  })
  .then((res: { settings: Config }) => {
    const config: Config = Object.assign(new Config(), res.settings);

    // Before anything else touches the DOM or the network: a <meta> policy governs only
    // what the document fetches after it is inserted, so every millisecond spent here is
    // a millisecond the application runs unprotected.
    installCsp(config);

    if (!isLocalEnv) {
      const sentryEnvironment = resolveSentryEnvironmentFromConfig(config.clientId);
      Sentry.init({
        dsn: 'https://c511333b2f1e1f53c9ea2e74fee974fc@o1071378.ingest.us.sentry.io/4509518594834432',
        release: SENTRY_RELEASE,
        environment: sentryEnvironment,
        sendDefaultPii: true,
        integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
        tracesSampleRate: 0.2,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        beforeSend(event, hint) {
          const error = hint?.originalException;

          if (shouldDropSentryError(error) || isChunkLoadError(error)) {
            return null;
          }

          const sanitizedUrl = window.location.origin + window.location.pathname;
          const feature = resolveSentryFeatureTag(window.location.pathname);
          const language = sessionStorage.getItem('language') || 'en';

          event.tags = {
            ...event.tags,
            feature,
            url: sanitizedUrl,
            language
          };

          applySentryIdentityToEvent(event);

          return event;
        }
      });
      Sentry.startSession();

      window.addEventListener('beforeunload', () => {
        Sentry.endSession();
      });
    }

    void validate(config).then((errors: ValidationError[]) => {
      if (errors.length > 0) {
        console.error(`Bad application configuration provided! Please contact administrators, errors: ${errors.toString()}`);
      }
    });
    platformBrowserDynamic([{ provide: CONFIG, useValue: config }])
      .bootstrapModule(AppModule)
      .catch((err) => console.error(err));
  });
